import React, { useEffect, useState } from 'react';
import { fetchModules } from '../../services/Administracion/modulesService';
import { fetchMenuOptions, upsertMenuOption } from '../../services/Administracion/menuOptionsService';
import { MenuOption } from '../../types/menuOption';
import { Module } from '../../types/module';
import { useAuth } from '../../context/AuthContext';
import MenuOptionModal from './MenuOptionModal';

const MenuOptionList: React.FC = () => {
    const { user } = useAuth();
    const [modules, setModules] = useState<Module[]>([]);
    const [menuOptions, setMenuOptions] = useState<MenuOption[]>([]);
    const [selectedModuleId, setSelectedModuleId] = useState<number | 'all'>('all');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedMenuOption, setSelectedMenuOption] = useState<MenuOption>({ menu_id: 0, menu_name: '', module_id: 0, parent_id: 0, description: '', route_path: '', component_name: '', icon_class: '', is_active: true, created_by: '', date_created: '' });
    const [showMenuOptionModal, setShowMenuOptionModal] = useState<boolean>(false);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [menuOptions, modulesData] = await Promise.all([
                fetchMenuOptions(),
                fetchModules()
            ]);


            setModules(modulesData.filter((mod) => mod.is_active));
            setMenuOptions(menuOptions);
        } catch (error: any) {
            setError(error.message || "Error cargando opciones de menú y módulos");
        } finally {
            setLoading(false);
        }
    };

    const handleFilterModule = (module_id: number | 'all') => {
        setSelectedModuleId(module_id);
    };

    const filterMenuOptions = menuOptions.filter((menuOption: MenuOption) => {
        if (selectedModuleId === 'all') return true;
        return menuOption.module_id === selectedModuleId;
    });

    const handleCreateEditOption = (menuOption?: MenuOption) => {
        console.log('Opción de menú a editar: ', menuOption);
        setSelectedMenuOption(menuOption || { menu_id: 0, menu_name: '', module_id: 0, parent_id: 0, description: '', route_path: '', component_name: '', icon_class: '', is_active: true, created_by: '', date_created: '' });
        setShowMenuOptionModal(true);
    };

    const handleChangeStatusMenuOption = async (menuOption: MenuOption) => {
        if (!menuOption.menu_id) return;
        if (!window.confirm(`¿Está seguro de desactivar la opción de menú: ${menuOption.menu_name}?`)) return;
        menuOption.is_active = !menuOption.is_active;
        try {
            await upsertMenuOption(menuOption);
            loadInitialData();
        } catch (error: any) {
            setError(error.message || "Error al desactivar la opción de menú"); //error.message es el mensaje que viene del servidor
        }
    };

    const handleSaveMenuOption = async (menuOption: MenuOption) => {
        setLoading(true);
        try {
            if (menuOption.menu_id) {
                menuOption.updated_by = user?.User.email;
            } else {
                menuOption.created_by = user?.User.email || '';
            }
            await upsertMenuOption(menuOption);
            loadInitialData();
        } catch (error: any) {
            setError(error.message || "Error saving module");
        } finally {
            setShowMenuOptionModal(false);
            setLoading(false);
        }
    };


    if (loading) return <div className='container mt-4'>Cargando opciones de menú...</div>;
    if (error) return <div className='container mt-4 text-danger'>Error en el componente: {error}</div>;




    return (
        <div className='container mt-4'>
            <h4 className='mb-4'>Mantenimiento de Opciones de Menú</h4>
            <div className="row mb-3 d-flex align-items-center"> 
                <label htmlFor="moduleFilter" className="form-label">Filtrar por Módulo</label>
                <div className="col-auto">
                    <select
                        id="moduleFilter"
                        className="form-select"
                        value={selectedModuleId}
                        onChange={(e) => handleFilterModule(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                    >
                        <option value="all">Todos</option>
                        {modules.map((module: Module) => (
                            <option key={module.module_id} value={module.module_id}>
                                {module.module_name}
                            </option>
                        ))}
                    </select>
                </div>
                {/* <div className='col-auto align-self-end '> */}
                <div className='col-auto ms-auto'>
                    <button className='btn btn-primary' onClick={() => handleCreateEditOption()}>
                        Crear Opción de Menú
                    </button>
                </div>
            </div>
            <table className="table table-striped trable-hover">
                <thead>
                    <tr>
                        <th>Menu ID</th>
                        <th>Módulo</th>
                        <th>Nombre</th>
                        <th>Padre</th>
                        <th>Ruta</th>
                        <th>Componente</th>
                        <th>Activo</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {filterMenuOptions.map((menuOption: MenuOption) => {
                        //Obtener nombre del padre si existe
                        const parentOption = menuOptions.find((option) => option.menu_id === menuOption.parent_id);
                        //Obtener nombre del modulo
                        const module = modules.find((module) => module.module_id === menuOption.module_id);
                        return (
                            <tr key={menuOption.menu_id}>
                                <td>{menuOption.menu_id}</td>
                                <td>{module ? module.module_name : "-"}</td>
                                <td><i className={`${menuOption.icon_class} me-2`} />{menuOption.menu_name}</td>
                                <td>{parentOption ? parentOption.menu_name : "-"}</td>
                                <td>{menuOption.route_path}</td>
                                <td>{menuOption.component_name}</td>
                                <td>{menuOption.is_active ? 'Si' : 'No'}</td>
                                <td>
                                    <button className='btn btn-sm btn-secondary me-2' onClick={() => handleCreateEditOption(menuOption)}>Editar</button>
                                    <button
                                        className={`btn btn-sm ${menuOption.is_active ? 'btn-danger' : 'btn-success'}`}
                                        onClick={() => handleChangeStatusMenuOption(menuOption)}
                                    >
                                        {menuOption.is_active ? 'Desactivar' : 'Activar'}
                                    </button>
                                </td>
                            </tr>)
                    })}
                </tbody>
            </table>
            {showMenuOptionModal && selectedMenuOption && (
                <MenuOptionModal
                    menuOption={selectedMenuOption}
                    allMenuOptions={menuOptions}
                    modules={modules}
                    onClose={() => setShowMenuOptionModal(false)}
                    onSave={handleSaveMenuOption}
                />
            )}
        </div>
    );
}

export default MenuOptionList;